import React from "react";
import { Dropdown } from "react-bootstrap";
import { useAuth } from "../../contexts/AuthContext";
import { useRegion } from "../../contexts/RegionContext";
import { APP_REGIONS, USER_ROLES } from "../../constants";
import { featureFlags, EnvironmentNames } from "../../featureFlags";
import styles from "./index.module.scss";

interface RegionSelectorProps {
  onRegionChange?: (region: string | null) => void;
}

const RegionSelector: React.FC<RegionSelectorProps> = ({ onRegionChange }) => {
  const { assignedRoles, assignedCountries, isLoading, error } = useAuth();
  const { selectedRegion, setSelectedRegion } = useRegion();

  // Hide region selector when admin authorization is disabled
  const stage = process.env.REACT_APP_STAGE as EnvironmentNames;
  if (!featureFlags[stage]?.enableAdminAuthorization) {
    return null;
  }

  const getAccessibleRegions = (): Array<[string, string]> => {
    if (!assignedRoles || !assignedCountries) {
      // Likely means the user is not logged in
      return [];
    }

    if (assignedRoles.includes(USER_ROLES.SUPER_ADMIN)) {
      return Object.entries(APP_REGIONS);
    }

    if (assignedRoles.includes(USER_ROLES.CUSTOMER_SUPPORT)) {
      return assignedCountries.flatMap((countryCode) => {
        const regionName = APP_REGIONS[countryCode];
        return regionName
          ? [[countryCode, regionName] as [string, string]]
          : [];
      });
    }

    return [];
  };

  const handleRegionChange = (regionCode: string | null) => {
    setSelectedRegion(regionCode);
    onRegionChange?.(regionCode);
  };

  if (isLoading) {
    return (
      <div
        className={styles.container}
        data-testid="region-selector-loading-message"
      >
        Loading...
      </div>
    );
  }

  if (error || !assignedRoles || !assignedCountries) {
    return (
      <div
        className={styles.container}
        data-testid="region-selector-error-message"
      >
        Error loading user permissions
      </div>
    );
  }

  const accessibleRegions = getAccessibleRegions();

  if (accessibleRegions.length === 0) {
    return (
      <div className={styles.container}>
        <span className={styles.label} data-testid="region-selector-label">
          View region:
        </span>
        <span
          className={styles.staticRegion}
          data-testid="region-selector-no-region-access"
        >
          No region access
        </span>
      </div>
    );
  }

  // If customer support has only 1 region, show static text
  if (
    assignedRoles.includes(USER_ROLES.CUSTOMER_SUPPORT) &&
    accessibleRegions.length === 1
  ) {
    const [, regionName] = accessibleRegions[0];
    return (
      <div className={styles.container}>
        <span className={styles.label} data-testid="region-selector-label">
          View region:
        </span>
        <span
          className={styles.staticRegion}
          data-testid="region-selector-static-region"
        >
          {regionName}
        </span>
      </div>
    );
  }

  // Show dropdown for multiple regions
  const getDisplayText = () => {
    if (!selectedRegion) {
      return `All ${accessibleRegions.length} regions`;
    }
    return APP_REGIONS[selectedRegion];
  };

  return (
    <div className={styles.container}>
      <span className={styles.label} data-testid="region-selector-label">
        View region:
      </span>
      <Dropdown
        className={styles.dropdown}
        data-testid="region-selector-dropdown"
      >
        <Dropdown.Toggle
          variant="outline-secondary"
          className={styles.dropdownToggle}
          id="region-selector-dropdown"
          data-testid="region-selector-dropdown-toggle"
        >
          <span>{getDisplayText()}</span>
          <span
            className={`material-icons material-icons-outlined ${styles.chevronIcon}`}
            data-testid="region-selector-chevron-icon"
          >
            chevron_right
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu className={styles.dropdownMenu}>
          <Dropdown.Item
            key="all-regions"
            onClick={() => handleRegionChange(null)}
            className={styles.dropdownMenuItem}
            data-testid="region-selector-all-regions-option"
          >
            All {accessibleRegions.length} regions
          </Dropdown.Item>
          {accessibleRegions.map(([countryCode, regionName]) => (
            <Dropdown.Item
              key={countryCode}
              onClick={() => handleRegionChange(countryCode)}
              className={styles.dropdownMenuItem}
              data-testid={`region-selector-region-option-${countryCode}`}
            >
              {regionName}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default RegionSelector;
